import { useEffect, useState, useMemo } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { listProjects } from '../../api/projects';
import { fmt } from '../../utils/format';
import type { GrandProjet } from '../../types/estimation';
import { useTranslation } from 'react-i18next';
import { TOOLTIP_STYLE } from '../charts/chartTheme';
import { InfoTip } from '../shared/InfoTip';
import styles from './ComparisonPage.module.css';

const PROJECT_COLORS = ['#6366f1', '#22d3ee', '#f39c12', '#9b59b6'];
const MAX_PROJECTS = 4;
const MIN_PROJECTS = 2;

interface ComparisonField {
  key: keyof GrandProjet;
  labelKey: string;
  format?: (v: number) => string;
  group: string;
}

const FIELDS: ComparisonField[] = [
  { key: 'logements', labelKey: 'logements', group: 'Programme' },
  { key: 'emplois', labelKey: 'emplois', group: 'Programme' },
  { key: 'ha_total', labelKey: 'totalAreaHa', group: 'Programme' },
  { key: 'ha_industriel', labelKey: 'industrielHa', group: 'Programme' },
  { key: 'horizon_years', labelKey: 'horizonYears', group: 'Programme' },
  { key: 'sbp_logements', labelKey: 'sbpLogements', format: (v) => `${fmt(v)} m\u00B2`, group: 'SBP' },
  { key: 'sbp_activites', labelKey: 'sbpActivites', format: (v) => `${fmt(v)} m\u00B2`, group: 'SBP' },
  { key: 'sbp_equipements', labelKey: 'sbpEquipements', format: (v) => `${fmt(v)} m\u00B2`, group: 'SBP' },
  { key: 'sbp_total', labelKey: 'sbpTotal', format: (v) => `${fmt(v)} m\u00B2`, group: 'SBP' },
];

// Radar chart parameters — chosen for diverse, meaningful comparison
const RADAR_FIELDS: { key: keyof GrandProjet; labelKey: string }[] = [
  { key: 'logements', labelKey: 'logements' },
  { key: 'emplois', labelKey: 'emplois' },
  { key: 'ha_total', labelKey: 'areaHa' },
  { key: 'sbp_logements', labelKey: 'sbpLogements' },
  { key: 'sbp_activites', labelKey: 'sbpActivites' },
  { key: 'sbp_equipements', labelKey: 'sbpEquipements' },
  { key: 'sbp_total', labelKey: 'sbpTotal' },
  { key: 'horizon_years', labelKey: 'horizonYears' },
];

// Bar chart groups — each rendered as its own chart with proper scale
const BAR_GROUPS: { titleKey: string; metrics: { key: keyof GrandProjet; labelKey: string }[] }[] = [
  {
    titleKey: 'programmeMetrics',
    metrics: [
      { key: 'logements', labelKey: 'logements' },
      { key: 'emplois', labelKey: 'emplois' },
    ],
  },
  {
    titleKey: 'areaMetrics',
    metrics: [
      { key: 'ha_total', labelKey: 'totalAreaHa' },
      { key: 'ha_industriel', labelKey: 'industrielHa' },
    ],
  },
  {
    titleKey: 'sbpMetrics',
    metrics: [
      { key: 'sbp_logements', labelKey: 'sbpLogements' },
      { key: 'sbp_activites', labelKey: 'sbpActivites' },
      { key: 'sbp_equipements', labelKey: 'sbpEquipements' },
      { key: 'sbp_total', labelKey: 'sbpTotal' },
    ],
  },
];

function getVal(project: GrandProjet, key: keyof GrandProjet): number {
  const v = project[key];
  return typeof v === 'number' ? v : 0;
}

export function ComparisonPage() {
  const { t } = useTranslation(['comparison', 'common']);
  const [allProjects, setAllProjects] = useState<GrandProjet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>(['', '']);

  useEffect(() => {
    listProjects()
      .then((data) => {
        setAllProjects(data);
        if (data.length >= 2) {
          setSelectedIds([data[0]!.id, data[1]!.id]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectedProjects = useMemo(
    () => selectedIds.map((id) => allProjects.find((p) => p.id === id)).filter(Boolean) as GrandProjet[],
    [allProjects, selectedIds]
  );

  function setProjectAt(index: number, id: string) {
    setSelectedIds((prev) => {
      const next = [...prev];
      next[index] = id;
      return next;
    });
  }

  function addProject() {
    if (selectedIds.length >= MAX_PROJECTS) return;
    setSelectedIds((prev) => [...prev, '']);
  }

  function removeProject(index: number) {
    if (selectedIds.length <= MIN_PROJECTS) return;
    setSelectedIds((prev) => prev.filter((_, i) => i !== index));
  }

  const groups = useMemo(() => {
    const g: Record<string, ComparisonField[]> = {};
    for (const f of FIELDS) {
      if (!g[f.group]) g[f.group] = [];
      g[f.group]!.push(f);
    }
    return g;
  }, []);

  // Radar chart data: normalize each metric to 0-100 scale (per-axis max)
  const radarData = useMemo(() => {
    if (selectedProjects.length < 2) return [];
    return RADAR_FIELDS.map((field) => {
      const values = selectedProjects.map((p) => getVal(p, field.key));
      const maxVal = Math.max(...values, 1);
      const entry: Record<string, string | number> = {
        metric: t(`comparison:${field.labelKey}`),
      };
      for (let i = 0; i < selectedProjects.length; i++) {
        entry[selectedProjects[i]!.name] = Math.round((values[i]! / maxVal) * 100);
        entry[`${selectedProjects[i]!.name}_raw`] = values[i]!;
      }
      return entry;
    });
  }, [selectedProjects, t]);

  // Bar chart data: one chart per group, each metric is a separate bar cluster
  const barChartGroups = useMemo(() => {
    if (selectedProjects.length < 2) return [];
    return BAR_GROUPS.map((group) => {
      const data = group.metrics.map((m) => {
        const entry: Record<string, string | number> = { name: t(`comparison:${m.labelKey}`) };
        for (const p of selectedProjects) {
          entry[p.name] = getVal(p, m.key);
        }
        return entry;
      });
      return { titleKey: group.titleKey, data };
    });
  }, [selectedProjects, t]);

  const hasEnoughProjects = selectedProjects.length >= MIN_PROJECTS;

  if (loading) return <div className={styles.loading}>{t('common:loadingProjects')}</div>;

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>{t('comparison:title')}</h2>

      {/* Dynamic project selectors */}
      <div className={styles.selectors}>
        {selectedIds.map((id, index) => (
          <div key={index} className={styles.selectorGroup}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label className={styles.selectorLabel} style={{ color: PROJECT_COLORS[index] }}>
                {t('comparison:projectLabel', { letter: String.fromCharCode(65 + index) })}
              </label>
              {selectedIds.length > MIN_PROJECTS && (
                <button
                  onClick={() => removeProject(index)}
                  style={{
                    background: 'transparent', border: 'none', color: 'var(--color-text-muted)',
                    cursor: 'pointer', fontSize: 14, padding: '0 4px', lineHeight: 1,
                  }}
                  title={t('comparison:remove')}
                >
                  &times;
                </button>
              )}
            </div>
            <select
              className={styles.select}
              value={id}
              onChange={(e) => setProjectAt(index, e.target.value)}
            >
              <option value="">{t('comparison:selectProject')}</option>
              {allProjects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        ))}

        {selectedIds.length < MAX_PROJECTS && (
          <button
            className={styles.addBtn}
            onClick={addProject}
          >
            {t('comparison:addProject')}
          </button>
        )}
      </div>

      {hasEnoughProjects ? (
        <>
          {/* KPI Cards */}
          <div className={styles.kpiRow} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {(['logements', 'emplois', 'sbp_total', 'ha_total'] as const).map((key) => {
              const label = key === 'logements' ? t('comparison:logements') : key === 'emplois' ? t('comparison:emplois') : key === 'sbp_total' ? t('comparison:sbpTotal') : t('comparison:areaHa');
              return (
                <div key={key} className={styles.kpiCard}>
                  <div className={styles.kpiLabel}>{label}</div>
                  <div className={styles.kpiValues} style={{ flexDirection: 'column', gap: 4 }}>
                    {selectedProjects.map((p, i) => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: PROJECT_COLORS[i], flexShrink: 0,
                          }}
                        />
                        <span style={{ fontFamily: 'var(--font-heading)', fontSize: 16, color: PROJECT_COLORS[i] }}>
                          {fmt(getVal(p, key))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Radar chart — normalized multi-parameter comparison */}
          {radarData.length > 0 && (
            <div className={styles.chartSection}>
              <h3 className={styles.sectionTitle}>{t('comparison:radarComparison')}<InfoTip text={t('comparison:tooltips.radarComparison')} /></h3>
              <div className={styles.radarWrapper}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                    <PolarAngleAxis
                      dataKey="metric"
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={false}
                      axisLine={false}
                    />
                    {selectedProjects.map((p, i) => (
                      <Radar
                        key={p.id}
                        name={p.name}
                        dataKey={p.name}
                        stroke={PROJECT_COLORS[i]}
                        fill={PROJECT_COLORS[i]}
                        fillOpacity={0.15}
                        strokeWidth={2}
                      />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value: number, name: string, props: { payload?: Record<string, number> }) => {
                        const raw = props.payload?.[`${name}_raw`];
                        return [raw != null ? fmt(raw) : `${value}%`, name];
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Grouped bar charts — one per metric group, properly scaled */}
          <div className={styles.chartSection}>
            <h3 className={styles.sectionTitle}>{t('comparison:barComparison')}<InfoTip text={t('comparison:tooltips.barComparison')} /></h3>
            <div className={styles.barChartsGrid}>
              {barChartGroups.map((group) => (
                <div key={group.titleKey} className={styles.barChartCard}>
                  <div className={styles.barChartTitle}>{t(`comparison:${group.titleKey}`)}</div>
                  <div className={styles.barChartInner}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={group.data}
                        layout="vertical"
                        margin={{ top: 4, right: 20, bottom: 4, left: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
                        <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(v: number) => fmt(v)} />
                        <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={75} />
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          formatter={(val: number, name: string) => [fmt(val), name]}
                          cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                        />
                        {selectedProjects.map((p, i) => (
                          <Bar
                            key={p.id}
                            dataKey={p.name}
                            fill={PROJECT_COLORS[i]}
                            radius={[0, 2, 2, 0]}
                            maxBarSize={20}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed comparison table */}
          <div className={styles.tableSection}>
            <h3 className={styles.sectionTitle}>{t('comparison:detailedComparison')}<InfoTip text={t('comparison:tooltips.detailedComparison')} /></h3>
            {Object.entries(groups).map(([group, fields]) => (
              <div key={group}>
                <div className={styles.groupLabel}>{group}</div>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('comparison:metric')}</th>
                      {selectedProjects.map((p, i) => (
                        <th key={p.id} style={{ color: PROJECT_COLORS[i] }}>{p.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f) => {
                      const fmtVal = f.format ?? ((v: number) => fmt(v));
                      const values = selectedProjects.map((p) => getVal(p, f.key));
                      const maxVal = Math.max(...values);
                      return (
                        <tr key={f.key as string}>
                          <td>{t(`comparison:${f.labelKey}`)}</td>
                          {values.map((v, i) => (
                            <td
                              key={selectedProjects[i]!.id}
                              style={v === maxVal && maxVal > 0 ? { fontWeight: 600, color: PROJECT_COLORS[i] } : undefined}
                            >
                              {fmtVal(v)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Communes comparison */}
          <div className={styles.tableSection}>
            <h3 className={styles.sectionTitle}>{t('comparison:communes')}</h3>
            <div className={styles.communesRow} style={{ gridTemplateColumns: `repeat(${selectedProjects.length}, 1fr)` }}>
              {selectedProjects.map((p, i) => (
                <div key={p.id} className={styles.communeCol}>
                  <div className={styles.communeColLabel} style={{ color: PROJECT_COLORS[i] }}>{p.name}</div>
                  <div className={styles.communeList}>
                    {p.communes.length > 0 ? p.communes.join(', ') : '\u2014'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className={styles.empty}>
          {t('comparison:selectTwoProjects')}
        </div>
      )}
    </div>
  );
}
